import { useQuery } from "@tanstack/react-query";
import { getMe } from "@/api/auth";
import { useAuthStore } from "@/store/auth";
import type { IntegrationStatus, Me, PmsType } from "@/types";

export interface Tenant {
  me: Me | undefined;
  clinicId: string | null;
  role: Me["role"] | null;
  isAdmin: boolean;
  pmsType: PmsType;
  isEzyVet: boolean;
  isCornerstone: boolean;
  ezyvetSite: string | null;
  integrationStatus: IntegrationStatus | null;
}

/**
 * The signed-in user's clinic and where its data comes from. Served from
 * /auth/me (not the persisted auth store) so it updates when an admin
 * connects or disconnects a PMS — invalidate the ["me"] query after that.
 */
export function useTenant(): Tenant {
  const token = useAuthStore((s) => s.token);
  const storedClinicId = useAuthStore((s) => s.clinicId);
  const { data } = useQuery({
    queryKey: ["me"],
    queryFn: getMe,
    enabled: Boolean(token),
    staleTime: 5 * 60_000,
  });

  const pmsType = data?.pms_type ?? "standalone";
  return {
    me: data,
    clinicId: data?.clinic_id ?? storedClinicId,
    role: data?.role ?? null,
    isAdmin: data?.role === "admin",
    pmsType,
    isEzyVet: pmsType === "ezyvet",
    isCornerstone: pmsType === "cornerstone",
    ezyvetSite: data?.ezyvet_site ?? null,
    integrationStatus: data?.integration_status ?? null,
  };
}
